import { useMemo, useState, useEffect } from 'react';
import NumberHelpers from './../../app/number-helpers';
import { filterActiveTenants } from './../../utils/tenantUtils';

export const AddDCModal = ({ show, handleCancel, handleSave, tenants }) => {
    const [selectedTenants, setSelectedTenants] = useState([]);
    const [amount, setAmount] = useState('');
    const [type, setType] = useState('debit');
    const [reason, setReason] = useState('');
    const [hideInactive, setHideInactive] = useState(true);
    const [dateTime, setDateTime] = useState(new Date().toISOString().split('T')[0]);
    const numberHelpers = new NumberHelpers();

    const handleSubmit = e => {
        e.preventDefault();

        const utcDateTime = `${dateTime}T00:00:00Z`;
        const parsed = numberHelpers.safeParseFloat(amount, 2);
        const numericAmount = typeof parsed === 'number' ? parsed : parseFloat(parsed);
        if (!isFinite(numericAmount)) {
            alert('Please enter a valid amount.');
            return;
        }
        handleSave({
            tenantIds: selectedTenants,
            // Send positive amount; backend/type determines sign
            value: Math.abs(numericAmount),
            type: type,
            reason: reason,
            date: utcDateTime
        });
    };

    const visibleTenants = useMemo(() => hideInactive ? filterActiveTenants(tenants) : (tenants ?? []), [tenants, hideInactive]);

    const allVisibleSelected = useMemo(() => {
        if (!visibleTenants || visibleTenants.length === 0) return false;
        const visIds = new Set(visibleTenants.map(t => t.id));
        const visibleSelectedCount = selectedTenants.filter(id => visIds.has(id)).length;
        return visibleSelectedCount === visibleTenants.length;
    }, [visibleTenants, selectedTenants]);

    useEffect(() => {
        // Prune selection only when hiding inactive tenants
        if (!hideInactive) return;
        const visIds = new Set(visibleTenants.map(t => t.id));
        setSelectedTenants(prev => {
            const next = prev.filter(id => visIds.has(id));
            if (next.length === prev.length && next.every((id, i) => id === prev[i])) {
                return prev;
            }
            return next;
        });
    }, [visibleTenants, hideInactive]);

    const handleSelectAll = () => {
        if (!allVisibleSelected) {
            // If selecting all, add all tenant IDs to selectedTenants
            setSelectedTenants(visibleTenants.map(tenant => tenant.id));
        } else {
            // If deselecting all, clear selectedTenants
            setSelectedTenants([]);
        }
    };

    const handleTenantSelect = (tenantId) => {
        const updatedSelection = selectedTenants.includes(tenantId)
            ? selectedTenants.filter(id => id !== tenantId)
            : [...selectedTenants, tenantId];

        setSelectedTenants(updatedSelection);
    };


    if (!show) return null;

    return (
        <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add Bulk Debit/Credit</h5>
                        <button type="button" className="btn-close" onClick={handleCancel}></button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="form-label">Select Tenants</label>
                                <div className="form-check form-switch mb-2">
                                    <input className="form-check-input" type="checkbox" id="hideInactiveToggle" checked={hideInactive} onChange={() => setHideInactive(val => !val)} />
                                    <label className="form-check-label" htmlFor="hideInactiveToggle">Hide Inactive Tenants</label>
                                </div>
                                <hr className="my-2" />
                                <div className="form-check mb-2">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={allVisibleSelected}
                                        onChange={handleSelectAll}
                                        id="selectAllCheckbox"
                                    />
                                    <label className="form-check-label" htmlFor="selectAllCheckbox">
                                        Select All
                                    </label>
                                </div>
                                <div className="tenant-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {visibleTenants?.map(tenant => (
                                        <div className="form-check" key={tenant.id}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={selectedTenants.includes(tenant.id)}
                                                onChange={() => handleTenantSelect(tenant.id)}
                                            />
                                            <label className="form-check-label">
                                                {tenant.displayName}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Amount</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={amount}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        // Allow optional leading minus, digits, one optional decimal point
                                        if (/^-?\d*\.?\d*$/.test(val)) {
                                            setAmount(val);
                                            if (val && val.trim().startsWith('-')) {
                                                setType('debit');
                                            }
                                        }
                                    }}
                                    onBlur={() => {
                                        const normalized = numberHelpers.safeParseFloat(amount, 2);
                                        setAmount(`${typeof normalized === 'number' ? normalized.toFixed(2) : normalized}`);
                                    }}
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Type</label>
                                <select
                                    className="form-select"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    <option value="debit">Debit</option>
                                    <option value="credit">Credit</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Reason</label>
                                <textarea
                                    className="form-control"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={dateTime}
                                    onChange={(e) => setDateTime(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
                                <button type="submit" className="btn btn-primary">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};